import { Box, Typography } from '@mui/material';
import React, { useCallback, useRef, useState } from 'react';
import { failureMessages, validatePassword } from '/b2b/common/helpers/Password';

import { FormikTextField } from '/b2b/common/components/FormControls/TextField';
import FormikValidationForm from '/b2b/common/components/organisms/FormikValidationForm';
import InvalidIcon from '@mui/icons-material/Close';
import Loader from '/b2b/common/components/Loader';
import Locale from '/b2b/common/components/Locale';
import Portlet from '/b2b/common/components/Portlet';
import PortletContent from '/b2b/common/components/PortletContent';
import PropTypes from 'prop-types';
import ResponsiveDialog from '/b2b/common/components/ResponsiveDialog';
import ValidIcon from '@mui/icons-material/Check';
import { changePasswordSchema } from './Password.schema';
import { v4 as uuid } from 'uuid';

const Password = props => {
    const { isLoading, onUpdateUserPassword } = props;
    const formikBag = useRef();
    const formId = React.useRef(uuid());
    const handleSubmit = useRef(onUpdateUserPassword);
    const [passwordChecks, updatePasswordChecks] = useState(validatePassword('', true));
    const onPasswordChange = event => {
        updatePasswordChecks(validatePassword(event.target.value, true));
    };

    const [confirmSessionLogoutOpen, setConfirmSessionLogoutOpen] = useState(false);

    const handleCloseSessionLogoutDialog = useCallback(() => {
        setConfirmSessionLogoutOpen(false);
        handleSubmit.current &&
            handleSubmit.current({ ...formikBag.current.values, revoke: false });
    }, []);

    const handleConfirmSessionLogoutDialog = useCallback(() => {
        setConfirmSessionLogoutOpen(false);
        handleSubmit.current && handleSubmit.current({ ...formikBag.current.values, revoke: true });
    }, []);

    const handleUpdatePasswordSubmit = useCallback(() => {
        setConfirmSessionLogoutOpen(true);
    }, []);

    return (
        <Portlet>
            <PortletContent>
                <FormikValidationForm
                    formId={formId.current}
                    initialValues={{}}
                    validationSchema={changePasswordSchema}
                    onSubmit={handleUpdatePasswordSubmit}
                    title="password.title"
                    subtitle="password.update"
                    submitText="common.update"
                    formikRef={formikBag}
                    validateOnMount
                >
                    <Box
                        display="flex"
                        flexDirection="column"
                        width="100%"
                        flexGrow={1}
                        gap={0.5}
                        alignItems="center"
                        mt={4}
                        mb={1}
                        sx={theme =>
                            theme.breakpoints.down('lg') ? { justifyContent: 'center' } : {}
                        }
                    >
                        <FormikTextField
                            name="password"
                            allowShowHide
                            label="password.current"
                            autoComplete="current-password"
                            fullWidth
                            required
                            type="password"
                            mb={1}
                        />
                        <FormikTextField
                            autoComplete="off"
                            allowShowHide
                            fullWidth
                            label="common.fields.newPassword.label"
                            name="newPassword"
                            type="password"
                            onChange={onPasswordChange}
                            required
                        />
                        <Box p={1} alignContent="flex-start" width="100%">
                            {props.failureMessages.map(([key, message]) => (
                                <Typography gutterBottom key={key} variant="body2">
                                    {passwordChecks[key] ? (
                                        <ValidIcon
                                            color="success"
                                            sx={{ verticalAlign: 'middle' }}
                                        />
                                    ) : (
                                        <InvalidIcon
                                            color="error"
                                            sx={{ verticalAlign: 'middle' }}
                                        />
                                    )}
                                    <Box
                                        ml={1}
                                        component="span"
                                        sx={{ lineHeight: 1.5, verticalAlign: 'middle' }}
                                    >
                                        <Locale path={message} />
                                    </Box>
                                </Typography>
                            ))}
                        </Box>
                        <FormikTextField
                            autoComplete="confirm-password"
                            fullWidth
                            allowShowHide
                            label="password.confirmNew"
                            name="confirmNewPassword"
                            type="password"
                            required
                        />
                    </Box>
                </FormikValidationForm>
            </PortletContent>
            <ResponsiveDialog
                title="auth.session.logout.title"
                open={confirmSessionLogoutOpen}
                onClose={() => setConfirmSessionLogoutOpen(false)}
                onCancel={handleCloseSessionLogoutDialog}
                onConfirm={handleConfirmSessionLogoutDialog}
                cancelText="common.no"
                confirmText="common.yes"
            >
                <Typography marginBottom={2} color="error">
                    <Locale path="auth.session.logout.body" />
                </Typography>
                <Typography marginBottom={2}>
                    <Locale path="auth.session.logout.cta" />
                </Typography>
            </ResponsiveDialog>
            <Loader loading={isLoading} />
        </Portlet>
    );
};

Password.propTypes = {
    className: PropTypes.string,
    failureMessages: PropTypes.array.isRequired,
    isLoading: PropTypes.bool,
    onUpdateUserPassword: PropTypes.func.isRequired,
};

Password.defaultProps = { failureMessages };

export default Password;
